/**
 * Estoque Page - Stock Consolidated View
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StockTable } from "@/components/stock/StockTable";
import { StockHistoryDialog } from "@/components/stock/StockHistoryDialog";
import { StockSummaryCards } from "@/components/stock/StockSummaryCards";
import { ProductViewDialog } from "@/components/products/ProductViewDialog";

import { Search, RefreshCw } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { estoqueAdapter, categoriesAdapter, productsAdapter } from "@/data";
import { useToast } from "@/hooks/use-toast";
import type { StockItem } from "@/types/stock";
import { supabase } from "@/integrations/supabase/client";

type SortField =
  | "produto_nome"
  | "categoria"
  | "quantidade_atual"
  | "custo_medio_temporal"
  | "valor_total_estoque";
type SortDirection = "asc" | "desc" | null;

export default function Estoque() {
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<StockItem[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [expiringItems, setExpiringItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Filtros
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  // Ordenação
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  // Dialogs
  const [selectedProduct, setSelectedProduct] = useState<StockItem | null>(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedProductForView, setSelectedProductForView] = useState<any>(null);
  const [selectedProductForDelete, setSelectedProductForDelete] = useState<any>(null);

  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [stockItems, search, categoryFilter]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [stockData, categoriesData, expiringData] = await Promise.all([
        estoqueAdapter.list({ limit: 1000 }),
        categoriesAdapter.list(),
        estoqueAdapter.getExpiring(30),
      ]);

      setStockItems(stockData.items);
      setCategories(categoriesData.items);
      setExpiringItems(expiringData);
    } catch (error: any) {
      console.error("Erro ao carregar estoque:", error);
      setStockItems([]); // Garante UI consistente
      
      // Toast APENAS para erros REAIS (RLS, network, etc.)
      if (error && typeof error === 'object') {
        const errorMessage = error?.message || '';
        const errorCode = error?.code || '';
        
        // Ignora "banco vazio" ou resultados vazios normais
        if (
          !errorMessage.toLowerCase().includes('no rows') &&
          !errorMessage.toLowerCase().includes('empty') &&
          errorCode !== 'PGRST116' // Supabase: no rows
        ) {
          toast({
            title: "Erro ao carregar estoque",
            description: errorMessage || "Não foi possível carregar os dados do estoque.",
            variant: "destructive",
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Supabase Realtime: escutar mudanças em estoque_saldo
  useEffect(() => {
    const channel = supabase
      .channel('estoque-realtime-changes')
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'estoque_saldo',
        },
        (payload) => {
          console.log('🔄 Estoque atualizado em tempo real:', payload);
          loadData(); // Recarregar dados automaticamente
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadData();
      toast({
        title: "Estoque atualizado",
        description: "Os dados foram recarregados com sucesso.",
      });
    } catch (error) {
      // Error already handled in loadData
    } finally {
      setIsRefreshing(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...stockItems];

    // Filtro de busca
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.produto_nome.toLowerCase().includes(searchLower) ||
          (item.categoria && item.categoria.toLowerCase().includes(searchLower)),
      );
    }

    // Filtro de categoria
    if (categoryFilter !== "all") {
      filtered = filtered.filter((item) => item.categoria === categoryFilter);
    }

    setFilteredItems(filtered);
  };

  const handleViewHistory = (item: StockItem) => {
    setSelectedProduct(item);
    setIsHistoryOpen(true);
  };

  const handleEditProduct = (productId: string) => {
    navigate(`/cadastro?edit=${productId}`);
  };

  const handleViewProduct = async (item: StockItem) => {
    try {
      const product = await productsAdapter.get(item.produto_id);
      setSelectedProductForView(product);
      setIsViewOpen(true);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar produto",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeleteProduct = (item: StockItem) => {
    const productToDelete = {
      id_produto: item.produto_id,
      nome: item.produto_nome,
    };
    setSelectedProductForDelete(productToDelete);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedProductForDelete) return;

    setIsDeleting(true);
    try {
      await productsAdapter.delete(selectedProductForDelete.id_produto);

      toast({
        title: "Produto excluído",
        description: `O produto ${selectedProductForDelete.nome} foi excluído com sucesso.`,
      });

      await loadData();
      setIsDeleteOpen(false);
      setSelectedProductForDelete(null);
    } catch (error: any) {
      toast({
        title: "Erro ao excluir produto",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortItems = (items: StockItem[]) => {
    if (!sortField || !sortDirection) return items;

    return [...items].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Campos numéricos
      if (
        sortField === "quantidade_atual" ||
        sortField === "custo_medio_temporal" ||
        sortField === "valor_total_estoque"
      ) {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      // Tratamento de valores nulos
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Comparação
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  const sortedItems = sortItems(filteredItems);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">Controle de Estoque</h1>
            <p className="text-muted-foreground mt-1">Resumo consolidado por produto</p>
          </div>
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
            {isRefreshing ? <LoadingSpinner size="sm" className="mr-2" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Atualizar
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-24">
            <LoadingSpinner size="lg" text="Carregando estoque..." />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <StockSummaryCards items={stockItems} expiringItemsCount={expiringItems.length} />

            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid gap-4 md:grid-cols-3">
                  {/* Search */}
                  <div className="md:col-span-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar por nome ou categoria..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>

                  {/* Category Filter */}
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todas as categorias" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as categorias</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id_categoria} value={cat.nome}>
                          {cat.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Results count */}
                <div className="mt-4 text-sm text-muted-foreground">
                  Exibindo {filteredItems.length} de {stockItems.length} produtos
                </div>
              </CardContent>
            </Card>

            {/* Stock Table */}
            <StockTable
              items={sortedItems}
              onViewHistory={handleViewHistory}
              onViewProduct={handleViewProduct}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
            />

            {/* History Dialog */}
            <StockHistoryDialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen} product={selectedProduct} />

            {/* Product View Dialog */}
            <ProductViewDialog
              open={isViewOpen}
              onOpenChange={setIsViewOpen}
              product={selectedProductForView}
              onEdit={() => {
                if (selectedProductForView) {
                  setIsViewOpen(false);
                  handleEditProduct(selectedProductForView.id_produto);
                }
              }}
            />
          </>
        )}
      </div>
    </Layout>
  );
}
